import type { PrV2MergeRequest } from '@/lib/pr-v2/types';

import type { PullRequestTarget } from './approve-pull-request';

import { fetchPrV2MergeabilityStatus, fetchPrV2Summary } from './fetch-pr-v2';

import { isPendingMergeabilityStatus } from '@/lib/hooks/mergeability-status/query-data';
import { getEffectiveMergeMethodFromStatus } from '@/lib/pull-requests/merge-method';
import { readStoredMergeMethod } from '@/lib/pull-requests/merge-method-preference';

export type PullRequestMergePlan =
  | { ready: true; merge: PrV2MergeRequest }
  | { ready: false; reason: string };

/**
 * Whether the viewer can merge the pull request right now from outside its page, with its checks
 * passed and nothing blocking it, and the merge the PR page's Merge button would make: the viewer's
 * preferred method where the repository allows it, the pull request's title and description, and
 * whether it goes through the base branch's merge queue.
 *
 * `headSha` is the head the viewer saw, so GitHub refuses the merge if the branch moved since. It
 * defaults to the current head.
 */
export async function planPullRequestMerge({
  owner,
  repo,
  number,
  headSha,
}: PullRequestTarget & { headSha?: string }): Promise<PullRequestMergePlan> {
  const [status, summary] = await Promise.all([
    fetchPrV2MergeabilityStatus(owner, repo, String(number)),
    fetchPrV2Summary(owner, repo, String(number)),
  ]);
  if (isPendingMergeabilityStatus(status)) {
    return { ready: false, reason: 'cubic is still checking whether it can be merged.' };
  }
  // Covers checks, approvals, conflicts, drafts and pull requests already merged.
  if (!status.canMerge) {
    return { ready: false, reason: status.humanReadableMergeStatus };
  }
  // GitHub merges a stacked pull request together with every open one below it. Only the PR page
  // says so, on its Merge button.
  if (status.stackPosition !== null && status.stackPosition > 1) {
    return { ready: false, reason: 'Merging it would also merge the PRs below it in its stack.' };
  }

  return {
    ready: true,
    merge: {
      title: summary.pullRequest.title ?? `PR #${number}`,
      body: summary.pullRequest.metadataSummary.body ?? '',
      sha: headSha ?? summary.pullRequest.headSha,
      merge_method: getEffectiveMergeMethodFromStatus(
        status.autoMerge.allowedMethods,
        status.requiresLinearHistory,
        readStoredMergeMethod({ owner, repo }) ?? 'merge',
      ),
      merge_action: status.mergeQueue === 'required' ? 'queue' : 'direct',
    },
  };
}
